// pages/settings/Settings.tsx
import { useEffect, useState } from "react";
import { 
  Card, Tabs, Switch, DatePicker, Button, Modal, message, Input,
  Table, Tag, TimePicker, Select, Divider, Alert, Checkbox, Radio, Spin, Flex 
} from "antd";
import { 
  ShopOutlined, 
  ClockCircleOutlined, 
  GlobalOutlined,
  FileTextOutlined,
  NotificationOutlined,
  EnvironmentOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux"; // ✅ ADD
import LocationSelector from "../../components/LocationSelector"; // ✅ ADD
import {
  useFetchSelfRestaurantDataQuery,
  useUpdateRestaurantSettingMutation,
} from "../restaurant/restaruant.service";
import {
  useGetMonthlyDueTimeQuery,
  useUpdateMonthlyDueTimeMutation,
  useGetChecklistVisibilityQuery,
  useUpdateChecklistVisibilityMutation,
  useGetHolidayStatusQuery,
  useGetOpeningHoursQuery,
  useUpdateOpeningHoursMutation,
  useEnableHolidayModeMutation,
  useDisableHolidayModeMutation,
} from "./setting.service";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./style.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const timezoneList = [
  { value: "Europe/London", label: "GMT+0:00 London, Edinburgh" },
  { value: "Europe/Paris", label: "GMT+1:00 Paris, Berlin" },
  { value: "Europe/Moscow", label: "GMT+2:00 Moscow, Istanbul" },
  { value: "Asia/Dubai", label: "GMT+4:00 Dubai, Abu Dhabi" },
  { value: "Asia/Karachi", label: "GMT+5:00 Karachi, Islamabad" },
  { value: "Asia/Dhaka", label: "GMT+6:00 Dhaka, Colombo" },
  { value: "Asia/Bangkok", label: "GMT+7:00 Bangkok, Jakarta" },
  { value: "Asia/Shanghai", label: "GMT+8:00 Beijing, Shanghai" },
  { value: "Asia/Tokyo", label: "GMT+9:00 Tokyo, Seoul" },
  { value: "Australia/Sydney", label: "GMT+10:00 Sydney, Melbourne" },
  { value: "Pacific/Auckland", label: "GMT+12:00 Auckland, Wellington" },
  { value: "America/New_York", label: "GMT-5:00 New York, Washington" },
  { value: "America/Chicago", label: "GMT-6:00 Chicago, Dallas" },
  { value: "America/Denver", label: "GMT-7:00 Denver, Phoenix" },
  { value: "America/Los_Angeles", label: "GMT-8:00 Los Angeles, San Francisco" },
  { value: "America/Anchorage", label: "GMT-9:00 Anchorage, Juneau" },
  { value: "Pacific/Honolulu", label: "GMT-10:00 Honolulu" },
];

export default function Settings() {
  const { t, i18n } = useTranslation();
  const user = useSelector((state) => state.app.user); // ✅ ADD

  // ✅ Location state
  const [locationId, setLocationId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [holidayRange, setHolidayRange] = useState(null);
  const [isHolidayModalVisible, setIsHolidayModalVisible] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [showReopenPrompt, setShowReopenPrompt] = useState(false);
  const { TabPane } = Tabs;

  // ✅ Set initial location from user
  useEffect(() => {
    const locId = user?.activeLocationId || user?.restaurantId;
    if (locId) {
      setLocationId(locId);
      setIsLoading(false);
    }
  }, [user]);

  // ✅ Handle location change
  const handleLocationChange = (newLocationId) => {
    setLocationId(newLocationId);
    // Refetch all data when location changes
    refetchRestaurant();
    refetchHours();
    refetchHoliday();
    refetchVisibility();
    refetchMonthly();
  };

  // ✅ All queries with locationId
  const {
    data: openingHoursData,
    error,
    isLoading: isHoursLoading,
    refetch: refetchHours
  } = useGetOpeningHoursQuery(locationId, {
    skip: !locationId,
    refetchOnMountOrArgChange: true,
  });

  const { 
    data: holidayStatus, 
    refetch: refetchHoliday,
    isLoading: isHolidayLoading 
  } = useGetHolidayStatusQuery(locationId, {
    skip: !locationId,
    refetchOnMountOrArgChange: true,
  });

  const [enableHoliday] = useEnableHolidayModeMutation();
  const [disableHoliday] = useDisableHolidayModeMutation();
  const { RangePicker } = DatePicker;

  const {
    data: restaurantData = {
      data: {
        restaurant: {
          alertEmail: "",
          sms: "",
          openingDueDay: [],
          closingDueDay: [],
          incidentDueDay: [],
          hotHoldDueDay: [],
          weeklyDueDay: [],
          openingDueTime: 0,
          closingDueTime: 0,
          incidentDueTime: 0,
          hotHoldDueTime: 0,
          weeklyDueTime: 0,
        },
      },
    },
    isSuccess,
    refetch: refetchRestaurant,
    isLoading: isRestaurantLoading,
  } = useFetchSelfRestaurantDataQuery(locationId, {
    skip: !locationId,
    refetchOnMountOrArgChange: true,
  });
  
  const restaurant = restaurantData?.data?.restaurant;
  const curLanguage = i18n.language;

  const [alertEmail, setAlertEmail] = useState();
  const [alertSMS, setAlertSMS] = useState("");
  const [openingCheck, setOpeningCheck] = useState([]);
  const [closingCheck, setClosingCheck] = useState([]);
  const [incident, setIncident] = useState([]);
  const [hotHoldChillCheck, setHotHoldChillCheck] = useState([]);
  const [weeklyCheck, setWeeklyCheck] = useState([]);
  const [openingCheckTime, setOpeningCheckTime] = useState(null);
  const [closingCheckTime, setClosingCheckTime] = useState(null);
  const [incidentTime, setIncidentTime] = useState(null);
  const [hotHoldChillCheckTime, setHotHoldChillCheckTime] = useState(null);
  const [weeklyCheckTime, setWeeklyCheckTime] = useState(null);
  const [timezone, setTimezone] = useState("Europe/London");
  
  const [monthlyScheduleOption, setMonthlyScheduleOption] = useState(1);
  const [firstDayTime, setFirstDayTime] = useState(null);
  const [lastDayTime, setLastDayTime] = useState(null);
  const [nextMonthlyDueDate, setNextMonthlyDueDate] = useState("");
  const [checklistVisibility, setChecklistVisibility] = useState({
    opening: true,
    closing: true,
    hothold: true,
    weekly: true,
    monthly: true,
    incident: true,
  });

  const [requestUpdateRestaurantSetting, { isSuccess: isUpdateSuccess, isLoading: isUpdateLoading }] = useUpdateRestaurantSettingMutation();
  
  const { 
    data: monthlyData, 
    refetch: refetchMonthly,
    isLoading: isMonthlyLoading 
  } = useGetMonthlyDueTimeQuery(locationId, {
    skip: !locationId,
    refetchOnMountOrArgChange: true,
  });
  
  const [updateMonthlyDueTime] = useUpdateMonthlyDueTimeMutation();
  
  const { 
    data: visibilityData, 
    isSuccess: isGetVisibilitySuccess, 
    refetch: refetchVisibility,
    isLoading: isVisibilityLoading 
  } = useGetChecklistVisibilityQuery(locationId, {
    skip: !locationId,
    refetchOnMountOrArgChange: true,
  });
  
  const [updateChecklistVisibility] = useUpdateChecklistVisibilityMutation();
  const [updateOpeningHours] = useUpdateOpeningHoursMutation();

  const handleChangeLanguage = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem("lng", language);
  };

  const handleHolidayRangeChange = (dates) => {
    setHolidayRange(dates);
  };

  useEffect(() => {
    if (isSuccess && restaurant) {
      setAlertEmail(restaurant.alertEmail);
      setAlertSMS(restaurant.sms ? `${restaurant.sms.slice(1)}` : "");
      setOpeningCheck(restaurant.openingDueDay || []);
      setClosingCheck(restaurant.closingDueDay || []);
      setHotHoldChillCheck(restaurant.hotHoldDueDay || []);
      setWeeklyCheck(restaurant.weeklyDueDay || []);
      setIncident(restaurant.incidentDueDay || []);
      
      const tz = restaurant.timezone || "UTC";
      setTimezone(tz);
      
      setOpeningCheckTime(restaurant.openingDueTime ? dayjs.tz(restaurant.openingDueTime, tz).format("HH:mm") : null);
      setClosingCheckTime(restaurant.closingDueTime ? dayjs.tz(restaurant.closingDueTime, tz).format("HH:mm") : null);
      setHotHoldChillCheckTime(restaurant.hotHoldDueTime ? dayjs.tz(restaurant.hotHoldDueTime, tz).format("HH:mm") : null);
      setWeeklyCheckTime(restaurant.weeklyDueTime ? dayjs.tz(restaurant.weeklyDueTime, tz).format("HH:mm") : null);
      setIncidentTime(restaurant.incidentDueTime ? dayjs.tz(restaurant.incidentDueTime, tz).format("HH:mm") : null);
    }
  }, [restaurant, isSuccess]);

  useEffect(() => {
    if (isGetVisibilitySuccess && visibilityData?.data) {
      setChecklistVisibility(visibilityData.data);
    }
  }, [visibilityData, isGetVisibilitySuccess]);

  useEffect(() => {
    if (monthlyData?.monthlyDueTime) {
      const tz = timezone;
      const dateInTz = dayjs.utc(monthlyData.monthlyDueTime).tz(tz);
      const dayOfMonth = dateInTz.date();
      const lastDayOfMonth = dateInTz.daysInMonth();
      const timeString = dateInTz.format("HH:mm");
      
      if (dayOfMonth === 1) {
        setMonthlyScheduleOption(1);
        setFirstDayTime(timeString);
        const nextDate = calculateNextDueDate(1, timeString);
        setNextMonthlyDueDate(nextDate);
      } else if (dayOfMonth === lastDayOfMonth) {
        setMonthlyScheduleOption(2);
        setLastDayTime(timeString);
        const nextDate = calculateNextDueDate(2, timeString);
        setNextMonthlyDueDate(nextDate);
      }
    }
  }, [monthlyData, timezone]);

  useEffect(() => {
    if (isUpdateSuccess) {
      refetchRestaurant();
    }
  }, [isUpdateSuccess, refetchRestaurant]);

  useEffect(() => {
    if (holidayStatus?.data?.shouldPromptReopen) setShowReopenPrompt(true);
  }, [holidayStatus]);

  const calculateNextDueDate = (option, timeString) => {
    if (!timeString) return "";
    const [hour, minute] = timeString.split(':').map(Number);
    const now = dayjs().tz(timezone);
    
    if (option === 1) {
      return now.add(1, 'month').date(1).hour(hour).minute(minute).second(0).format("MMMM D, YYYY [at] HH:mm");
    } else if (option === 2) {
      const nextMonth = now.add(1, 'month');
      const lastDay = nextMonth.daysInMonth();
      return nextMonth.date(lastDay).hour(hour).minute(minute).second(0).format("MMMM D, YYYY [at] HH:mm");
    }
    return "";
  };

  const handleVisibilityChange = async (type, checked) => {
    const previousVisibility = checklistVisibility;
    const newVisibility = { ...checklistVisibility, [type]: checked };
    setChecklistVisibility(newVisibility);
    try {
      // API expects flat flags: { opening, closing, hothold, weekly, monthly, incident }
      await updateChecklistVisibility(newVisibility).unwrap();
      window.dispatchEvent(new CustomEvent('settings-updated', { detail: { monthly: newVisibility.monthly } }));
    } catch (error) {
      console.error("Failed to update visibility:", error);
      setChecklistVisibility(previousVisibility);
      message.error(
        error?.data?.message || "Failed to update monthly checklist setting"
      );
    }
  };

  const handleScheduleChange = (option, time, timeString) => {
    if (!timeString) return;
    setMonthlyScheduleOption(option);
    if (option === 1) {
      setFirstDayTime(timeString);
    } else {
      setLastDayTime(timeString);
    }
    
    const now = dayjs().tz(timezone);
    let dueDate;
    
    if (option === 1) {
      dueDate = now.add(1, 'month').date(1).hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
    } else {
      const lastDay = now.daysInMonth();
      dueDate = now.date(lastDay).hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
    }
    
    const utcTimestamp = dueDate.utc().valueOf();
    const dueDay = option === 1 ? [1] : [];
    
    updateMonthlyDueTime({ 
      dueDay: dueDay, 
      dueTime: utcTimestamp,
      locationId // ✅ Pass locationId
    }).unwrap()
      .then((response) => {
        const nextDate = calculateNextDueDate(option, timeString);
        setNextMonthlyDueDate(nextDate);
      })
      .catch(error => console.error("Error:", error));
  };

  const handleDueDay = (type, dueDay) => {
    switch (type) {
      case "opening": 
        setOpeningCheck(dueDay); 
        requestUpdateRestaurantSetting({ 
          openingDueDay: dueDay,
          locationId // ✅ Pass locationId
        }); 
        break;
      case "closing": 
        setClosingCheck(dueDay); 
        requestUpdateRestaurantSetting({ 
          closingDueDay: dueDay,
          locationId // ✅ Pass locationId
        }); 
        break;
      case "hotHold": 
        setHotHoldChillCheck(dueDay); 
        requestUpdateRestaurantSetting({ 
          hotHoldDueDay: dueDay,
          locationId // ✅ Pass locationId
        }); 
        break;
      case "weekly": 
        setWeeklyCheck(dueDay); 
        requestUpdateRestaurantSetting({ 
          weeklyDueDay: dueDay,
          locationId // ✅ Pass locationId
        }); 
        break;
      case "incident": 
        setIncident(dueDay); 
        requestUpdateRestaurantSetting({ 
          incidentDueDay: dueDay,
          locationId // ✅ Pass locationId
        }); 
        break;
      default: break;
    }
  };

  const handleDueTime = (type, dueTime, dueTimeString) => {
    if (!dueTimeString || !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(dueTimeString)) return;
    switch (type) {
      case "opening": 
        setOpeningCheckTime(dueTimeString); 
        requestUpdateRestaurantSetting({ 
          openingDueTime: dueTimeString,
          locationId // ✅ Pass locationId
        }); 
        break;
      case "closing": 
        setClosingCheckTime(dueTimeString); 
        requestUpdateRestaurantSetting({ 
          closingDueTime: dueTimeString,
          locationId // ✅ Pass locationId
        }); 
        break;
      case "hotHold": 
        setHotHoldChillCheckTime(dueTimeString); 
        requestUpdateRestaurantSetting({ 
          hotHoldDueTime: dueTimeString,
          locationId // ✅ Pass locationId
        }); 
        break;
      case "weekly": 
        setWeeklyCheckTime(dueTimeString); 
        requestUpdateRestaurantSetting({ 
          weeklyDueTime: dueTimeString,
          locationId // ✅ Pass locationId
        }); 
        break;
      default: break;
    }
  };

  const handleTimezoneChange = (e) => {
    requestUpdateRestaurantSetting({ 
      timezone: e,
      locationId // ✅ Pass locationId
    });
  };

  const handleEnableHoliday = async () => {
    if (!holidayRange || !holidayRange[0] || !holidayRange[1]) {
      message.warning("Please select holiday dates first");
      return;
    }
    try {
      await enableHoliday({ 
        startDate: holidayRange[0].format("YYYY-MM-DD"), 
        endDate: holidayRange[1].format("YYYY-MM-DD"), 
        reason: "holiday",
        locationId // ✅ Pass locationId
      }).unwrap();
      message.success(`Holiday mode enabled until ${holidayRange[1].format("MMMM D, YYYY")}`);
      setIsHolidayModalVisible(false);
      refetchHoliday();
    } catch (error) {
      message.error("Failed to enable holiday mode");
    }
  };

  const handleDisableHoliday = async () => {
    try {
      await disableHoliday({ locationId }).unwrap(); // ✅ Pass locationId
      message.success("Holiday mode disabled. Kitchen is now open!");
      refetchHoliday();
      setShowReopenPrompt(false);
    } catch (error) {
      message.error("Failed to disable holiday mode");
    }
  };

  const handleUpdateHours = async (day, isOpen, openTime, closeTime) => {
    try {
      await updateOpeningHours({ 
        day, 
        isOpen, 
        openTime: openTime?.format("HH:mm") || "09:00", 
        closeTime: closeTime?.format("HH:mm") || "18:00",
        locationId // ✅ Pass locationId
      }).unwrap();
      message.success("Opening hours updated");
      refetchHours();
      setEditingDay(null);
    } catch (error) {
      message.error("Failed to update hours");
    }
  };

  const handleUpdateDay = async (day, isOpen) => {
    try {
      await updateOpeningHours({ 
        day, 
        isOpen,
        locationId // ✅ Pass locationId
      }).unwrap();
      message.success("Opening days updated");
      refetchHours();
      setEditingDay(null);
    } catch (error) {
      message.error("Failed to update");
    }
  };

  const columns = [
    { title: "Day", dataIndex: "dayName", key: "day", width: 150 },
    { 
      title: "Status", 
      dataIndex: "isOpen", 
      key: "status", 
      width: 120, 
      render: (isOpen) => <Tag color={isOpen ? "green" : "red"}>{isOpen ? "Open ✅" : "Closed ❌"}</Tag> 
    },
    { 
      title: "Action", 
      key: "action", 
      width: 100, 
      render: (_, record) => <Button size="small" onClick={() => setEditingDay(record)}>Change</Button> 
    }
  ];

  const RenderChecklistSection = ({ title, timeValue, onTimeChange, selectedDays, onDaysChange, type }) => (
    <div className="setting-page-module">
      <div className="setting-page-module-label">
        {title}
      </div>
      <div className="setting-page-module-select setting-page-module-alert">
        <div>
          {t("COMPLETED BY")}
          <TimePicker
            allowClear={false}
            style={{ marginLeft: "10px" }}
            format="HH:mm"
            value={timeValue ? dayjs(timeValue, "HH:mm") : null}
            onChange={(time, timeString) =>
              onTimeChange(type, time, timeString)
            }
          />
        </div>
        <div className="setting-page-module-select">
          <Checkbox.Group
            style={{ width: "100%" }}
            value={selectedDays}
            onChange={(e) => onDaysChange(type, e)}
          >
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <div key={day} className={`setting-page-module-select-item`}>
                <Checkbox value={day} />
                <div style={{ marginLeft: "10px" }}>
                  {t(["SUN", "MON", "TUES", "WED", "THU", "FRI", "SAT"][day])}
                </div>
              </div>
            ))}
          </Checkbox.Group>
        </div>
      </div>
    </div>
  );

  // ✅ Show loading state
  if (isLoading || !locationId) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "#999" }}>
            {!locationId ? "Please select a location" : "Loading settings..."}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      bodyStyle={{ padding: 0 }}
      title={
        <Flex align="center" gap="middle" wrap>
          <span>{t("Settings")}</span>
          <LocationSelector onLocationChange={handleLocationChange} />
        </Flex>
      }
    >
      <Spin spinning={isRestaurantLoading || isHoursLoading || isHolidayLoading || isMonthlyLoading || isVisibilityLoading}>
        <Tabs defaultActiveKey="general" type="card" style={{ marginBottom: 0 }}>
          {/* TAB 1: GENERAL */}
          <TabPane tab={<span><GlobalOutlined /> General</span>} key="general">
            <div style={{ padding: 24 }}>
              <div className="setting-page-module">
                <div className="setting-page-module-label">{t("LANGUAGE")}</div>
                <div className="setting-page-module-select">
                  <Select
                    style={{ width: "120px" }}
                    value={curLanguage}
                    options={[
                      { label: t("ENGLISH"), value: "en" },
                      { label: t("CHINESE"), value: "ch" },
                      { label: t("ITALIAN"), value: "it" },
                      { label: t("TURKISH"), value: "tu" },
                      { label: t("POLISH"), value: "po" },
                      { label: t("JAPANESE"), value: "jp" },
                      { label: t("HINDI"), value: "hi" },
                      { label: t("ARABIC"), value: "ar" },
                      { label: t("ROMANIAN"), value: "ro" },
                    ]}
                    onChange={handleChangeLanguage}
                  />
                </div>
              </div>

              <div className="setting-page-module">
                <div className="setting-page-module-label">{t("TIMEZONE")}</div>
                <div className="setting-page-module-select">
                  <Select
                    style={{ width: "300px" }}
                    value={timezone}
                    options={timezoneList}
                    onChange={handleTimezoneChange}
                    showSearch
                    filterOption={(input, option) =>
                      option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  />
                </div>
              </div>

              <div className="setting-page-module">
                <div className="setting-page-module-label">{t("ALERTS")}</div>
                <div className="setting-page-module-select setting-page-module-alert">
                  <div className="setting-page-module-select-item">
                    <div className="setting-page-module-select-item-label">EMAIL</div>
                    <Input
                      onChange={(e) => setAlertEmail(e.target.value)}
                      value={alertEmail}
                    />
                    <Button
                      style={{ marginLeft: "20px" }}
                      type="primary"
                      onClick={() =>
                        alertEmail &&
                        alertEmail !== "" &&
                        requestUpdateRestaurantSetting({ 
                          alertEmail,
                          locationId // ✅ Pass locationId
                        })
                      }
                    >
                      {t("UPDATE")}
                    </Button>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid #f0f0f0", fontSize: 12, color: "#999" }}>
                version 1.1
              </div>
            </div>
          </TabPane>

          {/* TAB 2: SCHEDULE & HOLIDAY */}
          <TabPane tab={<span><ShopOutlined /> Schedule</span>} key="schedule">
            <div style={{ padding: 24 }}>
              <Alert message="Regular Weekly Schedule" description="Set your kitchen's regular opening hours." type="info" showIcon style={{ marginBottom: 16 }} />
              <Table dataSource={openingHoursData?.data || []} columns={columns} pagination={false} rowKey="day" size="small" />
              
              <Divider />
              
              <Alert message="Temporary Closure" description="Use this for holidays, staff away, or any temporary kitchen closure." type="warning" showIcon style={{ marginBottom: 16 }} />
              
              {holidayStatus?.data?.isHolidayMode && (
                <Alert 
                  message="Holiday Mode Active" 
                  description={`Kitchen is currently closed until ${dayjs(holidayStatus.data.holidayEnd).format("MMMM D, YYYY")}`} 
                  type="error" 
                  showIcon 
                  style={{ marginBottom: 16 }} 
                  action={<Button size="small" danger onClick={handleDisableHoliday}>Reopen Now</Button>} 
                />
              )}
              
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>Select Holiday Period:</div>
                <RangePicker 
                  style={{ width: "100%" }} 
                  format="YYYY-MM-DD" 
                  value={holidayRange} 
                  onChange={handleHolidayRangeChange} 
                  disabled={holidayStatus?.data?.isHolidayMode} 
                  disabledDate={(current) => current && current < dayjs().startOf("day")} 
                  placeholder={["Start Date", "End Date"]} 
                />
              </div>
              
              {holidayRange && holidayRange[0] && holidayRange[1] && (
                <div style={{ marginBottom: 16, padding: 8, background: "#e6f7ff", borderRadius: 4 }}>
                  ✅ Selected: {holidayRange[0].format("MMMM D, YYYY")} to {holidayRange[1].format("MMMM D, YYYY")}
                </div>
              )}
              
              <Button 
                type="primary" 
                onClick={() => setIsHolidayModalVisible(true)} 
                disabled={holidayStatus?.data?.isHolidayMode || !holidayRange || !holidayRange[0] || !holidayRange[1]} 
                block
              >
                Start Holiday Mode
              </Button>
            </div>
          </TabPane>

          {/* TAB 3: CHECKLIST ALERTS & NOTIFICATIONS */}
          <TabPane tab={<span><NotificationOutlined /> Checklists & Alerts</span>} key="alerts">
            <div style={{ padding: 24 }}>
              <RenderChecklistSection 
                title="OPENING CHECKS" 
                timeValue={openingCheckTime} 
                onTimeChange={handleDueTime} 
                selectedDays={openingCheck} 
                onDaysChange={handleDueDay} 
                type="opening"
              />
              
              <RenderChecklistSection 
                title="PROBE CHECKS (Hot Hold / Chill)" 
                timeValue={hotHoldChillCheckTime} 
                onTimeChange={handleDueTime} 
                selectedDays={hotHoldChillCheck} 
                onDaysChange={handleDueDay} 
                type="hotHold"
              />
              
              <RenderChecklistSection 
                title="CLOSING CHECKS" 
                timeValue={closingCheckTime} 
                onTimeChange={handleDueTime} 
                selectedDays={closingCheck} 
                onDaysChange={handleDueDay} 
                type="closing"
              />
              
              <RenderChecklistSection 
                title="WEEKLY CHECKS" 
                timeValue={weeklyCheckTime} 
                onTimeChange={handleDueTime} 
                selectedDays={weeklyCheck} 
                onDaysChange={handleDueDay} 
                type="weekly"
              />
            </div>
          </TabPane>

          {/* TAB 4: MONTHLY CHECKLIST */}
          <TabPane tab={<span><FileTextOutlined /> Monthly Checklist & Alert</span>} key="monthly">
            <div style={{ padding: 24 }}>
              <div className="setting-page-module">
                <div className="setting-page-module-label">
                  {t("MONTHLY CHECKLIST OFF/ON")}
                </div>
                <div className="setting-page-module-select setting-page-module-alert">
                  <div className="setting-page-module-select-item">
                    <Switch
                      checked={checklistVisibility.monthly}
                      onChange={(checked) => handleVisibilityChange('monthly', checked)}
                    />
                    <span style={{ fontWeight: 'normal', marginLeft: '8px' }}>
                      {checklistVisibility.monthly ? t("ON") : t("OFF")}
                    </span>
                  </div>
                </div>
              </div>

              {checklistVisibility.monthly && (
                <div className="setting-page-module">
                  <div className="setting-page-module-label">
                    {t("MONTHLY SCHEDULE")}
                  </div>
                  <div className="setting-page-module-select setting-page-module-alert">
                    <div className="setting-page-module-select-item" style={{ marginBottom: '15px', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <Radio
                          checked={monthlyScheduleOption === 1}
                          onChange={() => setMonthlyScheduleOption(1)}
                          value={1}
                          name="monthlySchedule"
                        >
                          <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                            {t("First Day of Month")}
                          </span>
                        </Radio>
                      </div>
                      {monthlyScheduleOption === 1 && (
                        <div style={{ marginLeft: '28px' }}>
                          <TimePicker
                            allowClear={false}
                            format={"HH:mm"}
                            value={firstDayTime ? dayjs(firstDayTime, "HH:mm") : null}
                            onChange={(time, timeString) => handleScheduleChange(1, time, timeString)}
                            style={{ width: '120px' }}
                            placeholder="Select time"
                          />
                        </div>
                      )}
                    </div>
                    
                    <div className="setting-page-module-select-item" style={{ marginBottom: '15px', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <Radio
                          checked={monthlyScheduleOption === 2}
                          onChange={() => setMonthlyScheduleOption(2)}
                          value={2}
                          name="monthlySchedule"
                        >
                          <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                            {t("Last Day of Month")}
                          </span>
                        </Radio>
                      </div>
                      {monthlyScheduleOption === 2 && (
                        <div style={{ marginLeft: '28px' }}>
                          <TimePicker
                            allowClear={false}
                            format={"HH:mm"}
                            value={lastDayTime ? dayjs(lastDayTime, "HH:mm") : null}
                            onChange={(time, timeString) => handleScheduleChange(2, time, timeString)}
                            style={{ width: '120px' }}
                            placeholder="Select time"
                          />
                        </div>
                      )}
                    </div>
                    
                    {nextMonthlyDueDate && (
                      <div style={{ 
                        marginTop: '20px', 
                        padding: '10px', 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}>
                        <span style={{ fontWeight: 'bold' }}>{t("Next Due")}:</span>{' '}
                        {nextMonthlyDueDate}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </TabPane>
        </Tabs>
      </Spin>

      <Modal 
        title="Enable Holiday Mode" 
        open={isHolidayModalVisible} 
        onOk={handleEnableHoliday} 
        onCancel={() => setIsHolidayModalVisible(false)} 
        okText="Enable Holiday" 
        cancelText="Cancel"
      >
        <p>Please confirm that you want to enable holiday mode.</p>
        <p>During this period:</p>
        <ul>
          <li>✅ Kitchen will be marked as closed in reports</li>
          <li>✅ All checklists will be paused</li>
          <li>✅ System will prompt to reopen when holiday ends</li>
        </ul>
      </Modal>

      <Modal 
        title={`Change ${editingDay?.dayName} Status`} 
        open={!!editingDay} 
        onCancel={() => setEditingDay(null)} 
        footer={null}
      >
        {editingDay && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ marginBottom: 24 }}>
              <Switch 
                checked={editingDay.isOpen} 
                onChange={(checked) => setEditingDay({ ...editingDay, isOpen: checked })} 
                checkedChildren="Open ✅" 
                unCheckedChildren="Closed ❌"
                size="large"
              />
            </div>
            <div style={{ marginTop: 20, textAlign: "right" }}>
              <Button onClick={() => setEditingDay(null)}>Cancel</Button>
              <Button 
                type="primary" 
                style={{ marginLeft: 8 }} 
                onClick={() => handleUpdateDay(editingDay.day, editingDay.isOpen)}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal 
        title="🔄 Kitchen Reopening" 
        open={showReopenPrompt} 
        closable={false} 
        maskClosable={false} 
        footer={[
          <Button key="remind" onClick={() => setShowReopenPrompt(false)}>Remind Later</Button>,
          <Button key="reopen" type="primary" onClick={handleDisableHoliday}>Yes, Reopen Kitchen</Button>
        ]}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <ClockCircleOutlined style={{ fontSize: 48, color: "#faad14", marginBottom: 16 }} />
          <h3>Holiday Period Has Ended</h3>
          <p>Your kitchen was marked as away/holiday. Do you want to reopen it now?</p>
        </div>
      </Modal>
    </Card>
  );
}